import logoPodio from 'assets/img/2026/logo_podio.png';
import { ModalEditarCategoria } from 'components/ui/ModalEditarCategoria';
import { ModalEditarInscricao } from 'components/ui/ModalEditarInscricao';
import ModalWarning from 'components/ui/ModalWarning';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext/Modal.context';
import { useIsDateActive } from 'hooks/useControlDate';
import { useRule } from 'hooks/useRule';
import { IniciativaActionBar } from 'pages/InscricaoIniciativaElbb/components/IniciativaActionBar';
import { IniciativaDetalhes } from 'pages/InscricaoIniciativaElbb/components/IniciativaDetalhes';
import { IniciativaDirecionador } from 'pages/InscricaoIniciativaElbb/components/IniciativaDirecionador';
import { IniciativaHero } from 'pages/InscricaoIniciativaElbb/components/IniciativaHero';
import { IniciativaParticipantes } from 'pages/InscricaoIniciativaElbb/components/IniciativaParticipantes';
import React, { useEffect, useState } from 'react';
import { FaTrophy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { registrarConfirmar } from 'services/iniciativa-action.service';
import { confirmarParticipacaoElbb } from 'services/iniciativa-elbb.service';
import { IniciativaFormatada } from 'types/iniciativa';
import { PHASE_IDS } from 'utils/constants';
import { clearRegistrationUser } from 'utils/fns';
import { getClassificacaoLabel } from 'utils/iniciativa-categoria';

type IniciativaCardListagemProps = IniciativaFormatada & {
  onConfirm?: () => void;
  modoGE?: boolean;
  indicacaoExistente?: any;
  rankingMap?: Map<string, { points: number; posicao: number }>;
};

export const IniciativaCardListagem: React.FC<IniciativaCardListagemProps> = ({ onConfirm, modoGE, indicacaoExistente, rankingMap, ...props }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const rule = useRule();
  const { showModal, closeModal } = useModal();
  const inscricaoAberta = useIsDateActive(PHASE_IDS.CONEXAO_REGISTRATION_PERIOD);
  const [isSending, setIsSending] = useState(false);
  const [ruleUrl, setRuleUrl] = useState('');

  useEffect(() => {
    if (rule) setRuleUrl(rule.url);
  }, [rule]);

  const isInscrita = props.status === 'inscrita';
  const confirmedPlayers: string[] = (props.iniciativaOriginal as any)?.confirmedPlayers || [];
  const currentPlayer = clearRegistrationUser(user?._id || '');
  const isConfirmed = confirmedPlayers.some(
    (cp) => clearRegistrationUser(cp) === currentPlayer,
  );

  const needsIndication = (props.participantes?.length || 0) > 5;

  const handleEditarCategoria = () => {
    showModal(
      'editar-categoria',
      <ModalEditarCategoria
        iniciativaOriginal={props.iniciativaOriginal}
        onClose={closeModal}
        onSave={() => {
          closeModal();
          onConfirm?.();
        }}
      />,
      'editar-categoria'
    );
  };

  const handleEditarInscricao = () => {
    showModal(
      'editar-inscricao',
      <ModalEditarInscricao
        iniciativaOriginal={props.iniciativaOriginal}
        onClose={closeModal}
        onSave={() => {
          closeModal();
          onConfirm?.();
        }}
      />,
      'editar-inscricao'
    );
  };

  const handleIndicarGE = () => {
    navigate(`/confirmacao-participacao-premio/${props.id}`, { state: { iniciativa: props, modoGE: true } });
  };

  const handleConfirmarParticipacao = () => {
    if (needsIndication) {
      navigate(`/confirmacao-participacao-premio/${props.id}`, { state: { iniciativa: props } });
      return;
    }

    showModal(
      'confirm-participation',
      <ModalWarning
        rule={ruleUrl}
        title="Sua inscrição está quase pronta!"
        subTitle="Confirme abaixo algumas informações importantes antes de finalizá-la."
        needInfo={true}
        needRule={true}
        description={
          <div className="flex flex-col gap-3 text-base">
            <p>Declaro que li e concordo com os termos estabelecidos na{' '}
              <a href={ruleUrl} target="_blank" rel="noopener noreferrer" className="underline text-blue-bb-1 font-semibold hover:no-underline">403-1, item 7.2</a>.
            </p>
            <p>
              Estou ciente sobre os prazos de inscrição, requisitos de participação, critérios de avaliação e os meus direitos e deveres como participante do Prêmio BB.
            </p>
            <p>Comprometo-me a fornecer informações precisas e completas, além de seguir todas as regras e regulamentos da premiação.</p>
            <p>Autorizo a organização a usar minha imagem em fotos ou vídeos, bem como meus dados pessoais, para fins de divulgação.</p>
            <p>
              Tenho conhecimento de que será realizada a verificação dos requisitos de impedimentos por alguma das situações listadas na{' '}
              <a href={ruleUrl} target="_blank" rel="noopener noreferrer" className="underline text-blue-bb-1 font-semibold hover:no-underline">IN 403-1, item 7.2.8.2</a>,
              com base na situação apurada no primeiro dia útil após o encerramento das inscrições.
            </p>
          </div>
        }
        btnText="Confirmar participação"
        hasBackButton={true}
        onBackButton={closeModal}
        onClose={async () => {
          closeModal();
          setIsSending(true);
          try {
            await confirmarParticipacaoElbb(props.id, user!._id);

            try {
              await registrarConfirmar(user!._id, props.id);
            } catch (actionLogError) {
              console.warn('Falha ao registrar action_log confirmar participação:', actionLogError);
            }

            onConfirm?.();
          } catch (error) {
            console.error('Erro ao confirmar participação:', error);
          } finally {
            setIsSending(false);
          }
        }}
      />,
      'confirm-participation'
    );
  };

  const classif = getClassificacaoLabel({
    classificadaEtapa1: props.classificadaEtapa1,
    classificadaEtapa2: props.classificadaEtapa2,
    position: props.position,
  });

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden">
      {classif.isClassificada && (
        <div style={{
          background: '#3333BD',
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            color: '#F6F630',
            fontSize: '0.75rem',
            fontWeight: 800,
            letterSpacing: '0.5px',
            textTransform: 'uppercase' as const,
            whiteSpace: 'nowrap' as const,
          }}>
            {classif.isPodio ? (
              <img src={logoPodio} alt="" aria-hidden="true" style={{ height: 20 }} />
            ) : (
              <FaTrophy size={20} aria-hidden="true" />
            )}
            {classif.classificacao}
          </span>
          <span style={{
            padding: '0.5rem 1.25rem',
            borderRadius: '100px',
            fontSize: '0.813rem',
            fontWeight: 700,
            color: 'white',
            letterSpacing: '0.3px',
            textTransform: 'capitalize' as const,
            backgroundColor: props.corCategoria === 'green-bb' ? '#4DB6AC'
              : props.corCategoria === 'blue-bb-6' ? '#2F80ED'
              : props.corCategoria === 'pink-bb' ? '#FF6E91'
              : props.corCategoria === 'yellow-bb' ? '#FBC02D'
              : '#2F80ED',
            ...(props.corCategoria === 'yellow-bb' ? { color: '#0F054C' } : {}),
          }}>
            {props.categoria}
          </span>
        </div>
      )}
      <div className="p-7 sm:p-4">
      <div className={`grid grid-cols-1 ${(props.documentacao || props.videoUrl) ? 'md:grid-cols-[340px_1fr]' : ''} gap-8 items-start`}>
        {/* Coluna esquerda — mídia (quando há documentação ou vídeo) */}
        {(props.documentacao || props.videoUrl) && (
          <div className="flex flex-col gap-5">
            <IniciativaHero
              imagem={props.imagem}
              titulo={props.titulo}
              categoria={props.categoria}
              corCategoria={props.corCategoria}
              documentacao={props.documentacao}
              videoUrl={props.videoUrl}
              originalVideoUrl={props.originalVideoUrl}
              totalElogios={props.totalElogios}
              totalCompartilhamentos={props.totalCompartilhamentos}
              totalFavoritos={props.totalFavoritos}
              elogiosPorTipo={props.elogiosPorTipo}
            >
              {/* {isInscrita && (
                <button
                  onClick={handleEditarInscricao}
                  className="bg-transparent border-0 p-0 text-blue-bb-1 text-sm font-semibold cursor-pointer underline hover:no-underline self-start flex items-center gap-1"
                >
                  Editar
                  <IoPencil size={14} />
                </button>
              )} */}
            </IniciativaHero>
          </div>
        )}

        {/* Coluna direita — detalhes */}
        <div className="flex flex-col gap-5">
          <IniciativaDetalhes
            tema={props.tema}
            descricao={props.descricao}
            categoria={props.categoria}
            corCategoria={props.corCategoria}
            cadastradoPor={props.cadastradoPor}
            cadastradoMatricula={props.cadastradoMatricula}
            cadastradoData={props.cadastradoData}
            inscritoPor={props.inscritoPor}
            inscritoMatricula={props.inscritoMatricula}
            inscritoData={props.inscritoData}
            hideBadge={classif.isClassificada}
          />

          <IniciativaDirecionador
            direcionadorEstrategico={props.categoria}
            texto={props.iniciativaOriginal?.result || props.texto}
          />

          <IniciativaParticipantes
            participantes={props.participantes}
            rankingMap={modoGE ? rankingMap : undefined}
            totalValidos={props.totalValidos}
            totalOriginal={props.totalOriginal}
            validadeApurada={props.validadeApurada}
          />

          <IniciativaActionBar
            id={props.id}
            titulo={props.titulo}
            descricao={props.descricao}
            totalElogios={props.totalElogios}
            totalCompartilhamentos={props.totalCompartilhamentos}
            totalFavoritos={props.totalFavoritos}
            usuarioElogio={props.usuarioElogio}
            usuarioFavoritou={props.usuarioFavoritou}
            usuarioCompartilhou={props.usuarioCompartilhou}
          />
        </div>
      </div>

      <div className="pt-5 flex flex-col gap-3">
        {modoGE ? (
          indicacaoExistente ? (
            <button
              disabled
              className="w-full bg-gray-100 text-gray-500 border border-gray-300 rounded-lg px-11 py-3.5 text-[15px] font-extrabold cursor-not-allowed tracking-wide"
            >
              INDICAÇÃO REALIZADA
            </button>
          ) : (
            // <button
            //   disabled={isSending}
            //   onClick={handleIndicarGE}
            //   className="w-full bg-yellow-bb-1 text-blue-bb-1 border-0 rounded-lg px-11 py-3.5 text-[15px] font-extrabold cursor-pointer tracking-wide hover:bg-yellow-bb-5 hover:-translate-y-px transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            // >
            //   INDICAR PARTICIPANTES
            // </button>
            <></>
          )
        ) : isInscrita && isConfirmed ? (
          <button
            disabled
            className="w-full bg-gray-100 text-gray-500 border border-gray-300 rounded-lg px-11 py-3.5 text-[15px] font-extrabold cursor-not-allowed tracking-wide"
          >
            Participação confirmada
          </button>
        ) : isInscrita && !isConfirmed ? (
          // <button
          //   disabled={isSending}
          //   onClick={handleConfirmarParticipacao}
          //   className="w-full bg-yellow-bb-1 text-blue-bb-1 border-0 rounded-lg px-11 py-3.5 text-[15px] font-extrabold cursor-pointer tracking-wide hover:bg-yellow-bb-5 hover:-translate-y-px transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          // >
          //   CONFIRMAR INSCRIÇÃO
          // </button>
          <></>
        ) : (
          <>
            {/* {props.categoryPremio?.startsWith('ECBB') && (
              <button
                onClick={handleEditarCategoria}
                className="bg-transparent border-0 p-0 text-blue-bb-1 text-sm font-semibold cursor-pointer underline hover:no-underline self-start flex items-center gap-1"
              >
                Editar categoria
                <IoPencil size={14} />
              </button>
            )} */}
            {inscricaoAberta ? (
              <button
                className="w-full bg-yellow-bb-1 text-blue-bb-1 border-0 rounded-lg px-11 py-3.5 text-[15px] font-extrabold cursor-pointer tracking-wide hover:bg-yellow-bb-5 hover:-translate-y-px transition-all"
                onClick={() => navigate(`/inscricao-iniciativa-premio/${props.id}`, { state: { iniciativa: props } })}
              >
                INSCREVER INICIATIVA
              </button>
            ) : (
              <button
                disabled
                title="Fora do período de inscrição"
                className="w-full bg-gray-100 text-gray-500 border border-gray-300 rounded-lg px-11 py-3.5 text-[15px] font-extrabold cursor-not-allowed tracking-wide"
              >
                INSCRIÇÕES ENCERRADAS
              </button>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
};
